import { useState } from "react";
import { toast } from "sonner";
import { Copy, Download } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/site/Logo";
import { PRODUCTS } from "@/lib/site";
import { buildKit } from "@/lib/kit";

const BLOCKS = [["wordpress", "WordPress post (HTML)"], ["facebook", "Facebook post"], ["instagram", "Instagram caption"], ["youtube", "YouTube title, description & tags"]];

const copy = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed — select the text and copy manually"); }
};

const Block = ({ slug, id, label, text }) => (
  <div className="rounded-2xl border border-[#F2EDE4]/10 bg-[#0F2822] p-5" data-testid={`kit-${slug}-${id}`}>
    <div className="flex items-center justify-between gap-3">
      <p className="font-display text-lg font-semibold">{label}</p>
      <button onClick={() => copy(text)} data-testid={`kit-${slug}-${id}-copy`}
        className="inline-flex items-center gap-2 rounded-full bg-[#D96B43] px-4 py-2 text-xs font-semibold text-[#091D18] hover:bg-[#F2EDE4]">
        <Copy className="h-3.5 w-3.5" /> Copy
      </button>
    </div>
    <textarea readOnly value={text} rows={10} className="mt-4 w-full resize-y rounded-xl bg-[#091D18] p-4 font-mono text-xs leading-relaxed text-[#F2EDE4]/85 outline-none" />
  </div>
);

const Images = ({ slug }) => (
  <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2" data-testid={`kit-${slug}-images`}>
    {[["square", "Facebook / Instagram (1080×1080)"], ["youtube", "YouTube thumbnail (1280×720)"]].map(([t, l]) => (
      <a key={t} href={`/social/${slug}-${t}.jpg`} download data-testid={`kit-${slug}-download-${t}`}
        className="group overflow-hidden rounded-2xl border border-[#F2EDE4]/10 bg-[#0F2822]">
        <img src={`/social/${slug}-${t}.jpg`} alt={l} className={`${t === "square" ? "aspect-square" : "aspect-video"} w-full object-cover transition-transform duration-500 group-hover:scale-105`} />
        <span className="flex items-center gap-2 p-4 text-sm"><Download className="h-4 w-4 text-[#D96B43]" /> {l}</span>
      </a>
    ))}
  </div>
);

export default function Kit() {
  const [origin] = useState("https://gobbloo.in");
  return (
    <main className="min-h-screen bg-[#091D18] px-5 py-10 text-[#F2EDE4] sm:px-10" data-testid="kit-page">
      <div className="mx-auto max-w-5xl">
        <Logo testId="kit-logo" />
        <h1 className="mt-10 font-display text-4xl font-bold tracking-tight sm:text-5xl">Post kit</h1>
        <p className="mt-3 max-w-2xl text-[#F2EDE4]/75">Ready-to-paste posts for your website, Facebook, Instagram and YouTube. Each includes your hoplink, affiliate disclosure and health disclaimer.</p>
        <Tabs defaultValue="joint-genesis" className="mt-10">
          <TabsList className="flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
            {Object.entries(PRODUCTS).map(([slug, p]) => (
              <TabsTrigger key={slug} value={slug} data-testid={`kit-tab-${slug}`}
                className="rounded-full border border-[#F2EDE4]/20 px-5 py-2 text-[#F2EDE4] data-[state=active]:bg-[#F2EDE4] data-[state=active]:text-[#091D18]">
                {p.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {Object.keys(PRODUCTS).map((slug) => {
            const kit = buildKit(slug, origin);
            return (
              <TabsContent key={slug} value={slug} className="mt-8 space-y-5">
                <Images slug={slug} />
                {BLOCKS.map(([id, label]) => <Block key={id} slug={slug} id={id} label={label} text={kit[id]} />)}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
      <Toaster position="bottom-center" />
    </main>
  );
}
